import { useMemo, useState } from 'react';
import { AnimationControls, useStepper } from './AnimationControls';

function LabFrame({ title, eyebrow, children }: { title: string; eyebrow: string; children: React.ReactNode }) {
  return (
    <div className="lab-frame">
      <div className="lab-heading">
        <div><span className="section-kicker">{eyebrow}</span><h3>{title}</h3></div>
        <span className="live-badge"><i /> 可交互</span>
      </div>
      {children}
    </div>
  );
}

function SetupLab() {
  const steps = ['接通 3.3 V 与 GND', '连接 SWDIO / SWCLK / NRST', 'CubeIDE 生成工程', '编译产生 ELF', 'ST-Link 写入 Flash', '复位后进入 main', '断点暂停并观察变量'];
  const stepper = useStepper(steps.length, 1350);
  return <LabFrame eyebrow="工具链信号流" title="从源码到芯片执行">
    <div className="flow-rail" aria-label="开发流程图">{steps.map((step, index) => <div key={step} className={`flow-node ${index === stepper.index ? 'active' : ''} ${index < stepper.index ? 'passed' : ''}`}><span>{index + 1}</span>{step}</div>)}</div>
    <AnimationControls stepper={stepper} steps={steps} />
    <p className="lab-explanation">即使关闭动画，也可以从左到右阅读完整流程。下载与调试依赖 SWD，共地是所有信号有效的前提。</p>
  </LabFrame>;
}

function BitwiseLab() {
  const [valueText, setValueText] = useState('0xA5');
  const [maskText, setMaskText] = useState('0x0F');
  const [operation, setOperation] = useState<'AND' | 'OR' | 'XOR' | 'SHL' | 'SHR'>('AND');
  const value = Number.parseInt(valueText.replace(/^0x/i, ''), valueText.toLowerCase().startsWith('0x') ? 16 : 10) || 0;
  const mask = Number.parseInt(maskText.replace(/^0x/i, ''), maskText.toLowerCase().startsWith('0x') ? 16 : 10) || 0;
  const normalizedValue = value & 0xff;
  const normalizedMask = mask & 0xff;
  const result = ({ AND: normalizedValue & normalizedMask, OR: normalizedValue | normalizedMask, XOR: normalizedValue ^ normalizedMask, SHL: normalizedValue << Math.min(normalizedMask, 7), SHR: normalizedValue >>> Math.min(normalizedMask, 7) })[operation] & 0xff;
  const steps = ['解析输入为 8 位数', '把操作数对齐为二进制', '逐位应用逻辑或移位', '得到结果并显示十六进制'];
  const stepper = useStepper(steps.length);
  const bits = (n: number) => n.toString(2).padStart(8, '0').split('');
  return <LabFrame eyebrow="位运算实验台" title="让每一位变化都可见">
    <div className="lab-form bit-form">
      <label>数值<input value={valueText} onChange={(event) => setValueText(event.target.value)} inputMode="text" /></label>
      <label>{operation === 'SHL' || operation === 'SHR' ? '移位数' : '掩码'}<input value={maskText} onChange={(event) => setMaskText(event.target.value)} inputMode="text" /></label>
      <label>运算<select value={operation} onChange={(event) => setOperation(event.target.value as typeof operation)}><option>AND</option><option>OR</option><option>XOR</option><option>SHL</option><option>SHR</option></select></label>
    </div>
    <div className="bit-grid" aria-label="8位二进制结果">
      <div className="bit-label">VALUE</div>{bits(normalizedValue).map((bit, i) => <span key={`v${i}`}>{bit}</span>)}
      <div className="bit-label">MASK</div>{bits(normalizedMask).map((bit, i) => <span key={`m${i}`} className="mask-bit">{bit}</span>)}
      <div className="bit-label">RESULT</div>{bits(result).map((bit, i) => <span key={`r${i}`} className={bit === '1' ? 'on' : ''}>{bit}</span>)}
    </div>
    <div className="result-strip"><span>DEC {result}</span><strong>0x{result.toString(16).toUpperCase().padStart(2, '0')}</strong><span>BIN {result.toString(2).padStart(8, '0')}</span></div>
    <AnimationControls stepper={stepper} steps={steps} />
  </LabFrame>;
}

function ClockTreeLab() {
  const [hse, setHse] = useState(8);
  const [pllSource, setPllSource] = useState<'HSE' | 'HSI/2'>('HSE');
  const [hseDiv, setHseDiv] = useState(1);
  const [pllMul, setPllMul] = useState(9);
  const [sysSource, setSysSource] = useState<'HSI' | 'HSE' | 'PLL'>('PLL');
  const [ahbDiv, setAhbDiv] = useState(1);
  const [apb1Div, setApb1Div] = useState(2);
  const [apb2Div, setApb2Div] = useState(1);
  const pllIn = pllSource === 'HSE' ? hse / hseDiv : 4;
  const pll = pllIn * pllMul;
  const sysclk = sysSource === 'HSI' ? 8 : sysSource === 'HSE' ? hse : pll;
  const hclk = sysclk / ahbDiv;
  const pclk1 = hclk / apb1Div;
  const pclk2 = hclk / apb2Div;
  const tim1 = apb1Div === 1 ? pclk1 : pclk1 * 2;
  const tim2 = apb2Div === 1 ? pclk2 : pclk2 * 2;
  const clocks = [{ label: 'SYSCLK', value: sysclk, max: 72 }, { label: 'HCLK', value: hclk, max: 72 }, { label: 'PCLK1', value: pclk1, max: 36 }, { label: 'PCLK2', value: pclk2, max: 72 }, { label: 'APB1 TIM', value: tim1, max: 72 }, { label: 'APB2 TIM', value: tim2, max: 72 }];
  const invalidPll = pll > 72 || pll < 16;
  const steps = ['选择 HSI/HSE 源', 'PLL 输入预分频', 'PLL 倍频', '选择 SYSCLK', 'AHB 产生 HCLK', 'APB1/APB2 分频', '定时器应用 ×2 规则'];
  const stepper = useStepper(steps.length, 1450);
  const divs = [1, 2, 4, 8, 16];
  return <LabFrame eyebrow="真实 F103 规则" title="交互式时钟树">
    <div className="clock-layout">
      <div className="lab-form clock-form">
        <label>HSE MHz<input type="number" min="4" max="16" step="1" value={hse} onChange={(e) => setHse(Math.max(1, Number(e.target.value)))} /></label>
        <label>PLL 来源<select value={pllSource} onChange={(e) => setPllSource(e.target.value as typeof pllSource)}><option>HSE</option><option>HSI/2</option></select></label>
        <label>HSE 预分频<select value={hseDiv} disabled={pllSource !== 'HSE'} onChange={(e) => setHseDiv(Number(e.target.value))}><option value="1">/1</option><option value="2">/2</option></select></label>
        <label>PLL 倍频<select value={pllMul} onChange={(e) => setPllMul(Number(e.target.value))}>{Array.from({ length: 15 }, (_, i) => i + 2).map(n => <option key={n} value={n}>×{n}</option>)}</select></label>
        <label>SYSCLK 源<select value={sysSource} onChange={(e) => setSysSource(e.target.value as typeof sysSource)}><option>HSI</option><option>HSE</option><option>PLL</option></select></label>
        <label>AHB<select value={ahbDiv} onChange={(e) => setAhbDiv(Number(e.target.value))}>{[1, 2, 4, 8, 16, 64, 128, 256, 512].map(n => <option key={n} value={n}>/{n}</option>)}</select></label>
        <label>APB1<select value={apb1Div} onChange={(e) => setApb1Div(Number(e.target.value))}>{divs.map(n => <option key={n} value={n}>/{n}</option>)}</select></label>
        <label>APB2<select value={apb2Div} onChange={(e) => setApb2Div(Number(e.target.value))}>{divs.map(n => <option key={n} value={n}>/{n}</option>)}</select></label>
      </div>
      <div className="clock-tree-visual">
        <div className={`clock-chip ${stepper.index <= 1 ? 'active' : ''}`}>HSI 8 <small>MHz</small><br />HSE {hse} <small>MHz</small></div><i className="signal-line" />
        <div className={`clock-chip ${stepper.index === 2 ? 'active' : ''}`}>PLL<br /><strong>{pll.toFixed(1)}</strong> MHz</div><i className="signal-line" />
        <div className={`clock-chip ${stepper.index >= 3 ? 'active' : ''}`}>SYS<br /><strong>{sysclk.toFixed(1)}</strong> MHz</div>
      </div>
    </div>
    {invalidPll && sysSource === 'PLL' && <div className="warning-banner">PLL 输出 {pll.toFixed(1)} MHz 不在本课程推荐的 16–72 MHz 范围，请调整输入或倍频。</div>}
    <div className="metric-grid">{clocks.map(clock => <div key={clock.label} className={`metric ${clock.value > clock.max ? 'over' : ''}`}><span>{clock.label}</span><strong>{clock.value.toFixed(1)} MHz</strong><small>上限 {clock.max} MHz {clock.value > clock.max ? '· 已超频' : '· 合规'}</small></div>)}</div>
    <AnimationControls stepper={stepper} steps={steps} />
    <p className="lab-explanation">规则摘要：HSI 固定 8 MHz，作为 PLL 输入时先 /2；HSE 可直接或 /2 进入 PLL；APB 分频大于 1 时，对应定时器时钟为 2×PCLK。</p>
  </LabFrame>;
}

function LedLab() {
  const [high, setHigh] = useState(true);
  const [activeLow, setActiveLow] = useState(true);
  const lit = activeLow ? !high : high;
  const steps = ['GPIO 输出寄存器更新', '输出级拉高/拉低引脚', '回路出现或消失电位差', '电流经过限流电阻', 'LED 发光或熄灭'];
  const stepper = useStepper(steps.length);
  return <LabFrame eyebrow="电流路径" title="GPIO 与 LED 电路">
    <div className="segmented"><button className={!activeLow ? 'active' : ''} onClick={() => setActiveLow(false)}>高电平点亮</button><button className={activeLow ? 'active' : ''} onClick={() => setActiveLow(true)}>低电平点亮</button></div>
    <div className={`led-circuit ${lit ? 'lit' : ''}`}>
      <div className="mcu-pin"><span>STM32</span><strong>PC13</strong><small>{high ? '3.3 V · HIGH' : '0 V · LOW'}</small></div>
      <div className="wire"><i /></div><div className="resistor">330 Ω</div><div className="wire"><i /></div>
      <div className="led-symbol"><i /><span>{lit ? '发光' : '熄灭'}</span></div><div className="wire"><i /></div>
      <div className="rail">{activeLow ? '3.3 V' : 'GND'}</div>
    </div>
    <div className="lab-actions"><button className="button primary" onClick={() => setHigh(v => !v)}>切换 GPIO → {high ? 'LOW' : 'HIGH'}</button><code>HAL_GPIO_WritePin(GPIOC, GPIO_PIN_13, {high ? 'GPIO_PIN_SET' : 'GPIO_PIN_RESET'});</code></div>
    <AnimationControls stepper={stepper} steps={steps} />
    <p className="lab-explanation">当前采用{activeLow ? '灌电流（active-low）' : '拉电流（active-high）'}接法。动画中的流动只表示传统电流方向；实际接线必须串联限流电阻。</p>
  </LabFrame>;
}

function DebounceLab() {
  const [debounced, setDebounced] = useState(true);
  const raw = [1, 1, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 1, 1, 1, 1];
  const stable = [1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1];
  const steps = raw.map((value, i) => `${i} ms：原始=${value}，确认输出=${(debounced ? stable : raw)[i]}`);
  const stepper = useStepper(steps.length, 380);
  const flow = ['外部边沿', 'EXTI 挂起', 'NVIC 仲裁', 'IRQHandler', 'HAL 回调', '主循环事件'];
  return <LabFrame eyebrow="机械现实 → 软件事件" title="按键抖动与中断链">
    <div className="wave-panel">
      <div className="wave-label">RAW</div><div className="digital-wave">{raw.map((v, i) => <i key={i} className={`${v ? 'high' : 'low'} ${i === stepper.index ? 'cursor' : ''}`} />)}</div>
      <div className="wave-label">FILTER</div><div className="digital-wave filtered">{(debounced ? stable : raw).map((v, i) => <i key={i} className={`${v ? 'high' : 'low'} ${i === stepper.index ? 'cursor' : ''}`} />)}</div>
    </div>
    <label className="toggle-row"><input type="checkbox" checked={debounced} onChange={(e) => setDebounced(e.target.checked)} /><span>启用 5 ms 稳定窗口消抖</span></label>
    <div className="interrupt-flow">{flow.map((item, i) => <div key={item} className={i <= Math.floor(stepper.index / 3) ? 'active' : ''}>{item}{i < flow.length - 1 && <span>→</span>}</div>)}</div>
    <AnimationControls stepper={stepper} steps={steps} />
  </LabFrame>;
}

function TimerLab() {
  const [timerClock, setTimerClock] = useState(72);
  const [psc, setPsc] = useState(71);
  const [arr, setArr] = useState(999);
  const [ccr, setCcr] = useState(250);
  const frequency = timerClock * 1_000_000 / (psc + 1) / (arr + 1);
  const duty = Math.min(100, ccr / (arr + 1) * 100);
  const steps = ['定时器时钟进入 PSC', '计数器按分频后时钟递增', 'CNT 与 CCR 比较', '输出有效/无效电平', 'CNT 到 ARR 后产生更新'];
  const stepper = useStepper(steps.length, 900);
  return <LabFrame eyebrow="计数器实验台" title="定时器与 PWM 计算器">
    <div className="timer-layout">
      <div className="slider-stack">
        <label>定时器时钟 <b>{timerClock} MHz</b><input type="range" min="1" max="72" value={timerClock} onChange={e => setTimerClock(Number(e.target.value))} /></label>
        <label>PSC <b>{psc}</b><input type="range" min="0" max="719" value={psc} onChange={e => setPsc(Number(e.target.value))} /></label>
        <label>ARR <b>{arr}</b><input type="range" min="9" max="4999" value={arr} onChange={e => { const next = Number(e.target.value); setArr(next); setCcr(c => Math.min(c, next + 1)); }} /></label>
        <label>CCR <b>{ccr}</b><input type="range" min="0" max={arr + 1} value={ccr} onChange={e => setCcr(Number(e.target.value))} /></label>
      </div>
      <div className="pwm-viz">
        <div className="pwm-wave" style={{ '--duty': `${duty}%` } as React.CSSProperties}><i /><i /><i /></div>
        <div className="motor-disc" style={{ '--spin-time': `${Math.max(.25, 2.2 - duty / 55)}s`, opacity: .35 + duty / 155 } as React.CSSProperties}><span /></div>
        <small>模拟负载：{duty < 1 ? '停止' : duty < 35 ? '低速' : duty < 70 ? '中速' : '高速'}（真实电机需驱动器）</small>
      </div>
    </div>
    <div className="metric-grid three"><div className="metric"><span>计数频率</span><strong>{(timerClock * 1_000_000 / (psc + 1) / 1000).toFixed(2)} kHz</strong></div><div className="metric"><span>PWM 频率</span><strong>{frequency >= 1000 ? `${(frequency / 1000).toFixed(2)} kHz` : `${frequency.toFixed(2)} Hz`}</strong></div><div className="metric"><span>占空比</span><strong>{duty.toFixed(1)}%</strong></div></div>
    <AnimationControls stepper={stepper} steps={steps} />
  </LabFrame>;
}

function AdcLab() {
  const [voltage, setVoltage] = useState(1.65);
  const [vref, setVref] = useState(3.3);
  const code = Math.min(4095, Math.max(0, Math.round(voltage / vref * 4095)));
  const samples = useMemo(() => Array.from({ length: 8 }, (_, i) => Math.min(4095, Math.max(0, code + ((i * 37) % 17) - 8))), [code]);
  const steps = ['采样开关闭合', '内部电容追随输入', '逐次逼近量化 12 位', '结果进入 ADC_DR', 'DMA 请求搬运半字', '数组地址自动递增', 'CPU 在半/全回调处理'];
  const stepper = useStepper(steps.length, 1000);
  return <LabFrame eyebrow="模拟 → 数字" title="ADC 量化与 DMA 搬运">
    <div className="adc-layout">
      <div className="slider-stack"><label>输入电压 <b>{voltage.toFixed(2)} V</b><input type="range" min="0" max={vref} step="0.01" value={Math.min(voltage, vref)} onChange={e => setVoltage(Number(e.target.value))} /></label><label>参考电压 <b>{vref.toFixed(2)} V</b><input type="range" min="2.4" max="3.6" step="0.05" value={vref} onChange={e => { const next = Number(e.target.value); setVref(next); setVoltage(v => Math.min(v, next)); }} /></label></div>
      <div className="quantizer"><div className="analog-level" style={{ height: `${voltage / vref * 100}%` }} /><div className="quant-stairs">{Array.from({ length: 12 }, (_, i) => <i key={i} className={i / 12 < voltage / vref ? 'filled' : ''} />)}</div><strong>{code}</strong><span>12-bit code</span></div>
    </div>
    <div className="dma-flow"><div className={stepper.index < 4 ? 'active' : ''}>ADC_DR<br /><strong>{code}</strong></div><span>DMA →</span><div className="memory-array">{samples.map((sample, i) => <i key={i} className={i === Math.max(0, stepper.index - 4) ? 'active' : ''}>{sample}</i>)}</div></div>
    <div className="result-strip"><span>LSB ≈ {(vref / 4095 * 1000).toFixed(3)} mV</span><strong>{code}</strong><span>换算回 {(code * vref / 4095).toFixed(3)} V</span></div>
    <AnimationControls stepper={stepper} steps={steps} />
  </LabFrame>;
}

function UartLab() {
  const [character, setCharacter] = useState('A');
  const ascii = (character.codePointAt(0) ?? 65) & 0xff;
  const dataBits = Array.from({ length: 8 }, (_, i) => (ascii >> i) & 1);
  const frame = [0, ...dataBits, 1];
  const labels = ['START', ...dataBits.map((_, i) => `D${i}`), 'STOP'];
  const steps = frame.map((bit, i) => `${labels[i]}：发送 ${bit}，${i > 0 && i < 9 ? '数据最低位优先' : i === 0 ? '通知接收端帧开始' : '线路回到空闲高电平'}`);
  const stepper = useStepper(steps.length, 650);
  return <LabFrame eyebrow="115200 · 8-N-1" title="UART 帧逐位发送">
    <div className="uart-input"><label>输入字符<input maxLength={1} value={character} onChange={e => setCharacter(e.target.value || ' ')} /></label><div><span>ASCII</span><strong>0x{ascii.toString(16).toUpperCase().padStart(2, '0')}</strong><small>{ascii.toString(2).padStart(8, '0')}</small></div></div>
    <div className="uart-wave">{frame.map((bit, i) => <div key={i} className={`${bit ? 'high' : 'low'} ${i === stepper.index ? 'active' : ''}`}><span>{bit}</span><small>{labels[i]}</small></div>)}</div>
    <AnimationControls stepper={stepper} steps={steps} />
    <p className="lab-explanation">115200 baud 下每位约 8.68 µs；一帧 10 位约 86.8 µs。图中横向等宽表示每个 bit time 相同。</p>
  </LabFrame>;
}

function BusLab() {
  const [bus, setBus] = useState<'I2C' | 'SPI'>('I2C');
  const i2cSteps = ['SDA 在 SCL 高时下降：START', '发送 7 位地址 0x3C', '发送写位 W=0', '第 9 时钟从机拉低 ACK', '发送控制字节 0x00', '从机再次 ACK', 'SDA 在 SCL 高时上升：STOP'];
  const spiSteps = ['CS 拉低选中从机', 'SCK 从 CPOL=0 空闲态开始', 'MOSI 在下降沿更新 bit7', '从机在上升沿采样（CPHA=0）', 'MISO 同步返回一位', '重复 8 个时钟完成全双工字节', '等待 BSY 清零后 CS 拉高'];
  const steps = bus === 'I2C' ? i2cSteps : spiSteps;
  const stepper = useStepper(steps.length, 1050);
  return <LabFrame eyebrow="逐帧总线分析" title="I²C / SPI 时序播放器">
    <div className="segmented"><button className={bus === 'I2C' ? 'active' : ''} onClick={() => { if (bus !== 'I2C') stepper.reset(); setBus('I2C'); }}>I²C</button><button className={bus === 'SPI' ? 'active' : ''} onClick={() => { if (bus !== 'SPI') stepper.reset(); setBus('SPI'); }}>SPI Mode 0</button></div>
    <div className="bus-canvas">
      {(bus === 'I2C' ? ['SCL', 'SDA'] : ['CS', 'SCK', 'MOSI', 'MISO']).map((line, row) => <div className="bus-line" key={line}><strong>{line}</strong>{Array.from({ length: 14 }, (_, i) => <i key={i} className={`${(i + row + (bus === 'I2C' ? 0 : row)) % (row + 2) === 0 ? 'low' : 'high'} ${i <= stepper.index * 2 ? 'sent' : ''}`} />)}</div>)}
    </div>
    <div className="protocol-card"><span>当前帧</span><strong>{steps[stepper.index]}</strong><small>{bus === 'I2C' ? '两线、开漏、需要上拉；地址与 ACK 属于总线协议。' : '全双工、推挽；无统一 ACK，CS 与命令格式由器件定义。'}</small></div>
    <AnimationControls stepper={stepper} steps={steps} />
  </LabFrame>;
}

function PowerLab() {
  const states = ['运行 RUN', '保存上下文', '关闭非必要外设', '执行 WFI', 'STOP 低功耗', 'EXTI / RTC 唤醒', '恢复 PLL 与外设', '继续运行'];
  const stepper = useStepper(states.length, 1150);
  return <LabFrame eyebrow="功耗状态机" title="进入低功耗并可靠唤醒">
    <div className="state-diagram">{states.map((state, i) => <div key={state} className={`${i === stepper.index ? 'active' : ''} ${i < stepper.index ? 'passed' : ''}`}><span>{i + 1}</span><strong>{state}</strong>{i < states.length - 1 && <i>→</i>}</div>)}</div>
    <div className="power-meter"><span>相对功耗</span><div><i style={{ width: stepper.index === 4 ? '8%' : stepper.index === 3 || stepper.index === 5 ? '32%' : '88%' }} /></div><strong>{stepper.index === 4 ? '低' : stepper.index === 3 || stepper.index === 5 ? '转换中' : '运行级'}</strong></div>
    <AnimationControls stepper={stepper} steps={states} />
    <p className="lab-explanation">STOP 会保留 SRAM 和寄存器，但主时钟停止。唤醒不是“直接继续外设工作”：首先恢复系统时钟，然后才使用依赖波特率/定时的外设。</p>
  </LabFrame>;
}

function RtosLab() {
  const steps = ['Sensor 周期到期，就绪', 'Sensor 抢占并采样', 'Sensor 发送队列后阻塞', 'Display 因队列就绪', 'UART 高优先级中断发布事件', 'Comms 运行并发送', '各任务阻塞，Idle 运行'];
  const stepper = useStepper(steps.length, 1000);
  const taskStates = (task: string, i: number) => {
    const running = ['Sensor', 'Sensor', 'Sensor', 'Display', 'ISR', 'Comms', 'Idle'][i];
    if (task === running) return '运行';
    if (task === 'Display' && i < 3 || task === 'Comms' && i < 5) return '阻塞';
    return task === 'Idle' && i < 6 ? '就绪' : '就绪';
  };
  const tasks = ['ISR', 'Comms', 'Sensor', 'Display', 'Idle'];
  return <LabFrame eyebrow="单核调度视图" title="FreeRTOS 多任务时间轴">
    <div className="rtos-grid">{tasks.map(task => <div className="rtos-row" key={task}><strong>{task}</strong>{steps.map((_, i) => { const state = taskStates(task, i); return <span key={i} className={`${state} ${i === stepper.index ? 'current' : ''}`}>{state}</span>; })}</div>)}</div>
    <div className="legend"><span className="run">运行</span><span className="ready">就绪</span><span className="blocked">阻塞</span></div>
    <AnimationControls stepper={stepper} steps={steps} />
    <p className="lab-explanation">单核任一列只有一个任务处于“运行”。就绪任务可以竞争 CPU，阻塞任务在等待时间或事件，不消耗处理器时间。</p>
  </LabFrame>;
}

function DebugLab() {
  const [symptom, setSymptom] = useState('串口乱码');
  const guides: Record<string, string[]> = {
    '串口乱码': ['测量 3.3 V 并确认共地', '测 TX 单位位宽反推实际波特率', '核对 PCLK 与 BRR', '核对 8-N-1 与端子电平', '发送固定 0x55 观察交替波形', '最后检查字符串与缓冲逻辑'],
    '下载失败': ['确认目标板确有 3.3 V', '确认 NRST 未被拉低', '核对 SWDIO/SWCLK/GND', '降低 SWD 频率', 'Connect under reset', '检查读保护与 option bytes'],
    '中断不进': ['先读外设事件标志', '检查外设中断使能位', '检查 NVIC enable', '核对 IRQHandler 精确名称', '确认优先级未被屏蔽', '确认清标志方式正确'],
    '程序跑飞': ['查看复位原因', '停下并看 PC/LR/SP', '读取 Call Stack', '检查数组边界与栈水位', '读取 CFSR/HFSR', '最小化到可复现路径'],
  };
  const steps = guides[symptom];
  const stepper = useStepper(steps.length, 1300);
  return <LabFrame eyebrow="证据链诊断" title="从症状到根因">
    <div className="segmented wrap">{Object.keys(guides).map(key => <button key={key} className={symptom === key ? 'active' : ''} onClick={() => { if (key !== symptom) stepper.reset(); setSymptom(key); }}>{key}</button>)}</div>
    <div className="diagnostic-stack">{steps.map((step, i) => <div key={step} className={`${i === stepper.index ? 'active' : ''} ${i < stepper.index ? 'passed' : ''}`}><span>{i + 1}</span><strong>{step}</strong><small>{i === 0 ? '先验证底层前提' : i === steps.length - 1 ? '此时才进入业务代码' : '记录测量结果后再前进'}</small></div>)}</div>
    <AnimationControls stepper={stepper} steps={steps} />
  </LabFrame>;
}

function ProjectLab() {
  const projects = [
    { name: 'P1 交互控制器', parts: ['GPIO/EXTI', '事件队列', '命令解析', 'LED/日志'], test: '100 次按键、1 kB 串口零丢失' },
    { name: 'P2 传感显示站', parts: ['TIM 触发', 'ADC+DMA', '滤波换算', 'OLED+CSV'], test: '采样周期误差 <1%，断线有提示' },
    { name: 'P3 RTOS 环境终端', parts: ['传感任务', '队列/互斥', '显示通信', '健康监测'], test: '连续 8 h，卡死可复位追踪' },
  ];
  const [selected, setSelected] = useState(0);
  const steps = ['明确功能和量化验收', '核对硬件电压与接线', '搭建 BSP/Driver', '单模块冒烟测试', 'Service/App 集成', '故障注入与长稳测试'];
  const stepper = useStepper(steps.length, 1200);
  const project = projects[selected];
  return <LabFrame eyebrow="系统集成工作台" title="三个项目，逐阶提升">
    <div className="project-selector">{projects.map((projectItem, i) => <button key={projectItem.name} className={selected === i ? 'active' : ''} onClick={() => setSelected(i)}><span>0{i + 1}</span><strong>{projectItem.name}</strong></button>)}</div>
    <div className="architecture-flow">{project.parts.map((part, i) => <div key={part} className={i <= Math.floor(stepper.index / 2) ? 'active' : ''}>{part}{i < project.parts.length - 1 && <span>→</span>}</div>)}</div>
    <div className="acceptance"><span>当前验收标准</span><strong>{project.test}</strong></div>
    <AnimationControls stepper={stepper} steps={steps} />
  </LabFrame>;
}

export function ChapterLab({ chapterId }: { chapterId: number }) {
  const labs = [SetupLab, BitwiseLab, ClockTreeLab, LedLab, DebounceLab, TimerLab, AdcLab, UartLab, BusLab, PowerLab, RtosLab, DebugLab, ProjectLab];
  const Lab = labs[chapterId] ?? SetupLab;
  return <Lab />;
}
